/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { FlowIfEnabled } from '@/components/blocks/FlexFlow';
import { Linkable } from '@/components/blocks/Linkable';
import { MarketingConsentChoice } from '@/components/blocks/MarketingConsentChoice';
import { PasswordInput } from '@/components/blocks/PasswordInput';
import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import { eSpotRegisterContainerSX } from '@/components/content/Header/styles/departmentsOption';
import {
	formSx,
	passwordRequirementSX,
	RegisterSx,
} from '@/components/content/Register/styles/style';
import { useRegistration } from '@/data/Content/Registration';
import { useSessionDialog } from '@/data/Content/SessionDialog';
import { useWishLists } from '@/data/Content/WishLists';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { REGEX } from '@/data/constants/regex';
import { ID } from '@/data/types/Basic';
import { REG_EX } from '@/utils/address';
import { useForm } from '@/utils/useForm';
import {
	Box,
	Button,
	Checkbox,
	Divider,
	FormControlLabel,
	Grid,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { escapeRegExp } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { CDN_BASE_URL } from 'shared/custom/constants/commonConstants';

export enum Status {
	Neutral = 0,
	Error = 1,
	Success = 2,
}

const specialCharacters = /[-=_*?!@#$/(){}.,;:]/;
const upperCaseAlphabetLetters = /[A-ZÑ]/;
const lowerCaseAlphabetLetters = /[a-zñ]/;
const anyNumber = /\d/;

export const Register: FC<{ id: ID; isDialogView?: boolean }> = ({ isDialogView = false }) => {
	const theme = useTheme();
	const router = useNextRouter();
	const RouteLocal = useLocalization('Routes');
	const registerNLS = useLocalization('RegistrationLayout');
	const orderConfirmationRoute = RouteLocal.OrderConfirmation.route.t();
	const isThankYouPage = router.asPath
		.split('?')[0]
		.replace(/\/$/, '')
		.endsWith(`/${orderConfirmationRoute}`);
	const {
		registrationSubmit,
		initialRegistration,
		showVerificationTokenForm,
		resendCode,
		isTokenInvalid,
	} = useRegistration();
	const { onCreateWishListByDefault } = useWishLists();
	const {
		formRef,
		values: registrationValues,
		handleInputChange: originalHandleChange,
		handleSubmit,
		submitting,
	} = useForm(initialRegistration);
	const { openSessionDialog, sessionDialogOpen } = useSessionDialog();

	const [passwordValidations, setPasswordValidations] = useState({
		firstRequirement: Status.Neutral,
		secondRequirement: Status.Neutral,
		thirdRequirement: Status.Neutral,
		fourthRequirement: Status.Neutral,
		fifthRequirement: Status.Neutral,
	});

	const {
		firstRequirement,
		secondRequirement,
		thirdRequirement,
		fourthRequirement,
		fifthRequirement,
	} = passwordValidations;

	const tokenIsValid = useMemo(() => {
		if (showVerificationTokenForm) {
			const token = registrationValues.verificationToken || EMPTY_STRING;
			const isSixDigits = token.length === 6 && REGEX.IDENTIFICATION.test(token);
			return isSixDigits;
		}
		return false;
	}, [showVerificationTokenForm, registrationValues.verificationToken]);

	useEffect(() => {
		setPasswordValidations((prevState) => ({
			...prevState,
			firstRequirement:
				registrationValues.logonPassword.length > 7 ? Status.Success : Status.Neutral,
			secondRequirement: lowerCaseAlphabetLetters.test(registrationValues.logonPassword)
				? Status.Success
				: Status.Neutral,
			thirdRequirement: upperCaseAlphabetLetters.test(registrationValues.logonPassword)
				? Status.Success
				: Status.Neutral,
			fourthRequirement: anyNumber.test(registrationValues.logonPassword)
				? Status.Success
				: Status.Neutral,
			fifthRequirement: specialCharacters.test(registrationValues.logonPassword)
				? Status.Success
				: Status.Neutral,
		}));
	}, [registrationValues.logonPassword]);

	const handleOnBlurPassword = () => {
		setPasswordValidations((prevState) => ({
			...prevState,
			firstRequirement:
				prevState.firstRequirement === Status.Neutral ? Status.Error : prevState.firstRequirement,
			secondRequirement:
				prevState.secondRequirement === Status.Neutral ? Status.Error : prevState.secondRequirement,
			thirdRequirement:
				prevState.thirdRequirement === Status.Neutral ? Status.Error : prevState.thirdRequirement,
			fourthRequirement:
				prevState.fourthRequirement === Status.Neutral ? Status.Error : prevState.fourthRequirement,
			fifthRequirement:
				prevState.fifthRequirement === Status.Neutral ? Status.Error : prevState.fifthRequirement,
		}));
		setRegisterErrors?.((prevState) => ({
			...prevState,
			logonPassword:
				firstRequirement !== Status.Success ||
				secondRequirement !== Status.Success ||
				thirdRequirement !== Status.Success ||
				fourthRequirement !== Status.Success ||
				fifthRequirement !== Status.Success,
		}));
	};

	const validatedPassword = () =>
		firstRequirement === Status.Success &&
		secondRequirement === Status.Success &&
		thirdRequirement === Status.Success &&
		fourthRequirement === Status.Success &&
		fifthRequirement === Status.Success;

	const canCreate = (): boolean =>
		!/[A-Z]/.test(registrationValues.email!) &&
		REGEX.EMAIL.test(registrationValues?.email ?? EMPTY_STRING) &&
		registrationValues.email === registrationValues.verifyEmail &&
		registrationValues.firstName.trim() !== EMPTY_STRING &&
		registrationValues.lastName.trim() !== EMPTY_STRING &&
		validatedPassword() &&
		registrationValues.logonPassword === registrationValues.logonPasswordVerify;

	const handleResendCode = () => {
		const payload = { ...registrationValues, isDialogView };
		resendCode(payload);
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = event.target;

		if (name === 'verificationToken') {
			const numericValue = value.replace(/\D/g, '');
			const syntheticEvent = {
				...event,
				target: {
					...event.target,
					name,
					value: numericValue,
				},
			} as React.ChangeEvent<HTMLInputElement>;

			originalHandleChange(syntheticEvent);
			return;
		}

		originalHandleChange(event as React.ChangeEvent<HTMLInputElement>);
	};

	const [emailErrorMsg, setEmailErrorMsg] = useState(EMPTY_STRING);
	const [registerErrors, setRegisterErrors] = useState({
		email: false,
		verifyEmail: false,
		firstName: false,
		lastName: false,
		logonPassword: false,
		logonPasswordVerify: false,
	});

	/**
	* Revalidate the confirm-password error whenever either password changes.
	* This clears the stale mismatch error when the user corrects
	* the first password and both passwords become equal.
 	*/
	useEffect(() => {
    	if (registrationValues.logonPasswordVerify) {
        	setRegisterErrors((prevState) => ({
            	...prevState,
            	logonPasswordVerify:
	                registrationValues.logonPassword !==
                	registrationValues.logonPasswordVerify,
        	}));
    	}
	}, [
    	registrationValues.logonPassword,
    	registrationValues.logonPasswordVerify,
	]);

	const handleEmailBlur = () => {
		const { email } = registrationValues;
		const invalidFormat = !REGEX.EMAIL.test(registrationValues?.email ?? EMPTY_STRING);
		const invalidUppercaseFormat = /[A-Z]/.test(email!);

		if (email === '') {
			setRegisterErrors?.((prevState) => ({
				...prevState,
				email: false,
			}));
			setEmailErrorMsg(EMPTY_STRING);
			return;
		}

		if (invalidFormat) {
			setRegisterErrors?.((prevState) => ({
				...prevState,
				email: true,
			}));
			setEmailErrorMsg(registerNLS.Msgs.InvalidFormat.t());
			return;
		}

		if (invalidUppercaseFormat) {
			setRegisterErrors?.((prevState) => ({
				...prevState,
				email: true,
			}));
			setEmailErrorMsg(registerNLS.Msgs.InvalidUppercaseFormat.t());
			return;
		}

		setRegisterErrors?.((prevState) => ({
			...prevState,
			email: false,
		}));

		setEmailErrorMsg(EMPTY_STRING);
	};

	return (
		<Paper sx={RegisterSx.container} square>
			<Stack sx={RegisterSx.formContainer}>
				<Box sx={formSx(isDialogView)}>
					<Box
						component="form"
						onSubmit={handleSubmit(async (formData) => {
							const payload = { ...formData, isDialogView };
							const success = await registrationSubmit(payload);
							if(success)
								onCreateWishListByDefault();
							// On the thank-you page the session dialog can stay open, which would
							// otherwise skip the redirect. Always send the new user home from there.
							if (success === true && (!sessionDialogOpen || isThankYouPage)) {
								window.location.href = '/';
							}
						})}
						ref={formRef}
						autoComplete="off"
					>
						{!showVerificationTokenForm ? (
							<Stack spacing={2}>
								{!isDialogView ? (
									<Box display="flex">
										<Typography variant="headingSubhead" sx={RegisterSx.title} component="span">
											{registerNLS.Register.t()}
										</Typography>
										<Divider sx={RegisterSx.titleDivider} />
									</Box>
								) : null}
								<Grid container item>
									<Grid item xs={12} sm={6} sx={RegisterSx.gridItem}>
										<TextField
											autoFocus
											fullWidth
											margin="none"
											name="email"
											label={registerNLS.Email.t()}
											value={registrationValues.email}
											onChange={handleChange}
											inputProps={{
												maxLength: 100,
												type: 'email',
												placeholder:
													registrationValues.email === '' ? registerNLS.emailPlaceholder.t() : '',
												pattern: REG_EX.EMAIL.source,
												autoComplete: 'off',
											}}
											error={registerErrors.email}
											helperText={emailErrorMsg}
											sx={RegisterSx.input}
											FormHelperTextProps={{ sx: RegisterSx.helperText }}
											onBlur={handleEmailBlur}
											required
										/>
									</Grid>
									<Grid item xs={12} sm={6} sx={RegisterSx.gridItem}>
										<TextField
											fullWidth
											margin="none"
											name="verifyEmail"
											label={registerNLS.VerifyEmail.t()}
											value={registrationValues.verifyEmail}
											onChange={handleChange}
											inputProps={{
												maxLength: 100,
												type: 'email',
												placeholder: registerNLS.emailPlaceholder.t(),
												autoComplete: 'off',
											}}
											error={registerErrors.verifyEmail}
											helperText={
												registerErrors.verifyEmail
													? registerNLS.Msgs.NoMatchEmails.t()
													: EMPTY_STRING
											}
											placeholder={registerNLS.emailPlaceholder.t()}
											sx={RegisterSx.input}
											FormHelperTextProps={{ sx: RegisterSx.helperText }}
											onBlur={() =>
												setRegisterErrors?.((prevState) => ({
													...prevState,
													verifyEmail:
														registrationValues.email !== registrationValues.verifyEmail
															? true
															: false,
												}))
											}
											required
										/>
									</Grid>
								</Grid>

								<Grid container item sx={RegisterSx.gridContainerItem}>
									<Grid item xs={12} sm={6} sx={RegisterSx.gridItem}>
										<TextField
											margin="none"
											fullWidth
											name="firstName"
											label={registerNLS.FirstName.t()}
											error={registerErrors.firstName}
											value={registrationValues.firstName}
											onChange={handleChange}
											inputProps={{
												maxLength: 40,
												required: true,
												autoComplete: 'off',
											}}
											sx={RegisterSx.input}
											helperText={
												registerErrors.firstName
													? registerNLS.Msgs.NoFullRequirementsFirstName.t()
													: EMPTY_STRING
											}
											onBlur={() =>
												setRegisterErrors?.((prevState) => ({
													...prevState,
													firstName: registrationValues.firstName.trim() === EMPTY_STRING,
												}))
											}
											FormHelperTextProps={{ sx: RegisterSx.helperText }}
										/>
									</Grid>
									<Grid item xs={12} sm={6} sx={RegisterSx.gridItem}>
										<TextField
											margin="none"
											fullWidth
											name="lastName"
											label={registerNLS.LastName.t()}
											error={registerErrors.lastName}
											value={registrationValues.lastName}
											onChange={handleChange}
											inputProps={{
												maxLength: 40,
												required: true,
												autoComplete: 'off',
											}}
											sx={RegisterSx.input}
											helperText={
												registerErrors.lastName
													? registerNLS.Msgs.NoFullRequirementsLastName.t()
													: EMPTY_STRING
											}
											onBlur={() =>
												setRegisterErrors?.((prevState) => ({
													...prevState,
													lastName: registrationValues.lastName === EMPTY_STRING,
												}))
											}
											FormHelperTextProps={{ sx: RegisterSx.helperText }}
										/>
									</Grid>
								</Grid>

								<Grid container item sx={RegisterSx.gridContainerItem}>
									<Grid item xs={12} sx={RegisterSx.validationPasswordTitleContainer}>
										<Typography
											component="h1"
											variant="bodyBaseline"
											sx={RegisterSx.validationPasswordTitleText}
										>
											{registerNLS.RegistrationPasswordValidation.t()}
										</Typography>
									</Grid>
									<Grid item sm={12} md={4} sx={RegisterSx.validationPasswordTitleContainer}>
										<Typography
											component="ul"
											variant="bodyBaseline"
											sx={RegisterSx.passwordRequirement}
										>
											<Typography
												component="li"
												variant="bodySubText"
												id="first-requirement"
												sx={passwordRequirementSX(firstRequirement)}
											>
												{registerNLS.PasswordCriteria.Criteria1.t()}
											</Typography>
											<Typography
												component="li"
												variant="bodySubText"
												id="second-requirement"
												sx={passwordRequirementSX(secondRequirement)}
											>
												{registerNLS.PasswordCriteria.Criteria2.t()}
											</Typography>
											<Typography
												component="li"
												variant="bodySubText"
												id="third-requirement"
												sx={passwordRequirementSX(thirdRequirement)}
											>
												{registerNLS.PasswordCriteria.Criteria3.t()}
											</Typography>
										</Typography>
									</Grid>
									<Grid item sm={12} md={8} sx={RegisterSx.validationPasswordTitleContainer}>
										<Typography
											component="ul"
											variant="bodyBaseline"
											sx={RegisterSx.passwordRequirement}
										>
											<Typography
												component="li"
												variant="bodySubText"
												id="fourth-requirement"
												sx={passwordRequirementSX(fourthRequirement)}
											>
												{registerNLS.PasswordCriteria.Criteria4.t()}
											</Typography>
											<Typography
												component="li"
												variant="bodySubText"
												id="fifth-requirement"
												sx={passwordRequirementSX(fifthRequirement)}
											>
												{registerNLS.PasswordCriteria.Criteria5.t()}
											</Typography>
										</Typography>
									</Grid>
								</Grid>

								<Grid container item sx={RegisterSx.gridContainerItem}>
									<Grid item xs={12} sm={6} sx={RegisterSx.gridItem}>
										<PasswordInput
											name="logonPassword"
											label={registerNLS.Password.t()}
											value={registrationValues.logonPassword}
											onChange={handleChange}
											error={registerErrors.logonPassword}
											helperText={
												registerErrors.logonPassword
													? registerNLS.Msgs.NoFullRequirementsPassword.t()
													: ''
											}
											inputProps={{
												maxLength: 100,
												pattern: escapeRegExp(registrationValues.logonPasswordVerify),
												required: true,
												autoComplete: 'off',
											}}
											sx={RegisterSx.passwordInput}
											onBlur={handleOnBlurPassword}
										/>
									</Grid>
									<Grid item xs={12} sm={6} sx={RegisterSx.gridItem}>
										<PasswordInput
											name="logonPasswordVerify"
											label={registerNLS.VerifyPassword.t()}
											value={registrationValues.logonPasswordVerify}
											error={registerErrors.logonPasswordVerify}
											onChange={handleChange}
											helperText={
												registerErrors.logonPasswordVerify
													? registerNLS.Msgs.NoMatchPasswords.t()
													: EMPTY_STRING
											}
											inputProps={{
												maxLength: 100,
												pattern: escapeRegExp(registrationValues.logonPassword),
												autoComplete: 'off',
											}}
											sx={RegisterSx.passwordInput}
											onBlur={() =>
												setRegisterErrors?.((prevState) => ({
													...prevState,
													logonPasswordVerify:
														registrationValues.logonPassword !==
														registrationValues.logonPasswordVerify,
												}))
											}
										/>
									</Grid>
								</Grid>

								<Grid container item sx={RegisterSx.gridContainerItem}>
									<Grid item xs={12}>
										<FormControlLabel
											control={
												<Checkbox
													name="receiveEmail"
													checked={registrationValues.receiveEmail}
													onChange={handleChange}
												/>
											}
											label={registerNLS.TextContent.t()}
											sx={RegisterSx.checkboxLabel}
										/>
									</Grid>
								</Grid>

								<FlowIfEnabled feature={EMS_STORE_FEATURE.MARKETING_CONSENT}>
									<Stack>
										<MarketingConsentChoice
											name="marketingTrackingConsent"
											checked={registrationValues.marketingTrackingConsent || false}
											onChange={handleChange}
											value={registrationValues.marketingTrackingConsent || false}
										/>
									</Stack>
								</FlowIfEnabled>

								<Stack spacing={0} sx={RegisterSx.gridContainerItem}>
									<Stack>
										<Typography variant="bodyBaseline" sx={RegisterSx.termsConditionsText}>
											{registerNLS.AcceptTermsAndConditions.t()}{' '}
											<Linkable
												href={registerNLS.TermsAndConditionsURL.t()}
												type="link"
												id="register-form-terms-conditions-link"
												data-testid="register-form-terms-conditions-link"
												sx={RegisterSx.termsConditionsLink}
												target="_blank"
												rel="noopener noreferrer"
											>
												{registerNLS.TermsAndConditions.t()}
											</Linkable>{' '}
											{registerNLS.And.t()}{' '}
											<Linkable
												href={registerNLS.PrivacyURL.t()}
												type="link"
												id="register-form-privacy-link"
												data-testid="register-form-privacy-link"
												sx={RegisterSx.termsConditionsLink}
												target="_blank"
												rel="noopener noreferrer"
											>
												{registerNLS.Privacy.t()}
											</Linkable>
											.
										</Typography>
										<Button
											type="submit"
											variant="contained"
											sx={RegisterSx.button}
											disabled={!canCreate()}
											data-testid="button-register-submit"
											id="button-register-submit"
											fullWidth
										>
											{registerNLS.RegisterComplete.t()}
										</Button>
									</Stack>
								</Stack>
							</Stack>
						) : (
							<Stack spacing={2} pt={2}>
								<Typography variant="bodyBaseline" sx={RegisterSx.text}>
									{registerNLS.RegistrationSecondSection.t()}
								</Typography>
								<Grid sx={RegisterSx.imageContainer}>
									<img
										src={`${CDN_BASE_URL}/assets/img/reset-password/send-mail.svg`}
										alt="send-mail"
										width="150"
									></img>
								</Grid>
								<Typography variant="bodyBaseline" sx={RegisterSx.text}>
									{registerNLS.RegistrationSecondReminderSection.t()}
								</Typography>
								<Grid container>
									<Grid item xs={12} sm={6} pr={{ xs: 0, sm: 1 }}>
										<TextField
											disabled
											fullWidth
											margin="none"
											name="email"
											label={registerNLS.Email.t()}
											value={registrationValues.email}
										/>
									</Grid>
									<Grid item xs={12} sm={6} pl={{ xs: 0, sm: 1 }} mt={{ xs: 2, sm: 0 }}>
										<TextField
											fullWidth
											margin="none"
											required
											name="verificationToken"
											autoFocus
											label={registerNLS.VerificationToken.t()}
											placeholder={registerNLS.VerificationToken.t()}
											value={registrationValues.verificationToken}
											onChange={handleChange}
											inputProps={{
												maxLength: 6,
												pattern: '[0-9]*',
												inputMode: 'numeric',
											}}
											error={isTokenInvalid}
											helperText={
												isTokenInvalid ? registerNLS.VerificationErrorToken.t() : EMPTY_STRING
											}
										/>
									</Grid>
									<Divider />
									<Grid item xs={12} mt={{ xs: 2 }}>
										<Button
											type="submit"
											variant="contained"
											sx={RegisterSx.button}
											disabled={submitting || !tokenIsValid}
											data-testid="button-register-submit"
											id="button-register-submit"
											fullWidth
										>
											{registerNLS.RegistrationVerify.t()}
										</Button>
									</Grid>
								</Grid>
								<Grid sx={RegisterSx.resendReminder}>
									<Typography variant="bodySubText" sx={RegisterSx.text}>
										{registerNLS.RegistrationNoCode.t()}
									</Typography>
								</Grid>
								<Grid>
									<Button
										type="button"
										variant="outlined"
										sx={RegisterSx.button}
										disabled={submitting}
										data-testid="button-register-submit"
										id="button-resend-code"
										fullWidth
										onClick={handleResendCode}
									>
										{registerNLS.RegistrationResendCode.t()}
									</Button>
								</Grid>
							</Stack>
						)}
					</Box>
					{!showVerificationTokenForm ? (
						<Box pt={2}>
							{/* login links */}
							<Divider />
							<Box mt={2}>
								<Typography variant="bodyBaseline" sx={RegisterSx.text}>
									{registerNLS.Account.t()}{' '}
									{sessionDialogOpen ? (
										<Linkable
											type="inline"
											variant="outlined"
											sx={RegisterSx.link}
											data-testid="button-register-signin"
											id="button-register-signin"
											onClick={() => openSessionDialog('signIn')}
										>
											{registerNLS.SignIn.t()}
										</Linkable>
									) : (
										<Linkable
											type="inline"
											variant="outlined"
											sx={RegisterSx.link}
											data-testid="button-register-signin"
											id="button-register-signin-page"
											onMouseDown={() => (window.location.href = '/login')}
										>
											{registerNLS.SignIn.t()}
										</Linkable>
									)}
								</Typography>
							</Box>
						</Box>
					) : null}
				</Box>

				{!isDialogView ? (
					<Box sx={eSpotRegisterContainerSX(theme, isDialogView)}>
						<EMarketingSpot
							id={'Register_AdditionalInfo'}
							properties={{ emsName: 'Register_AdditionalInfo' }}
						/>
					</Box>
				) : null}
			</Stack>
		</Paper>
	);
};
